import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Banner,
  Box,
  SingleSelectButtonGroup,
  Stack,
  Text,
} from '@stitch-fix/mode-react';
import SizeGuideTable from '../../SizeGuideTable';

const SizeChart = ({ businessLine }: { businessLine: string | null }) => {
  const [selectedSubNavTab, setSelectedSubNavTab] = useState(
    businessLine === 'WOMENS' ? 'regular' : 'standard',
  );

  const subNavTabs = () => {
    if (businessLine === 'WOMENS') {
      return [
        { value: 'regular', label: 'Regular' },
        { value: 'plus', label: 'Plus' },
        { value: 'petite', label: 'Petite' },
      ];
    }

    return [
      { value: 'standard', label: 'Standard' },
      { value: 'short', label: 'Short' },
      { value: 'tall', label: 'Tall' },
    ];
  };

  const bannerText = () => {
    if (businessLine === null) {
      return '';
    }

    type TextMapping = {
      [key: string]: {
        [key: string]: string;
      };
    };

    const textMappings: TextMapping = {
      WOMENS: {
        regular: '',
        plus: 'Stitch Fix Plus size clothing is curated with your shape in mind. Items are designed to have more room in the bust, upper arm, waist, hip, and thigh.',
        petite:
          'Petite sizes are recommended for those 5′4″ and under. These items are designed to be shorter across body, sleeve, hemline, and inseam lengths.',
      },
      MENS: {
        standard: '',
        short:
          'Short sizes are designed to be shorter across the body, sleeve and inseam lengths.',
        tall: 'Tall sizes are designed to be longer across the body with extended sleeve, and inseam lengths.',
      },
    };

    return textMappings[businessLine][selectedSubNavTab];
  };

  const renderSizeChart = () => {
    if (businessLine === 'WOMENS') {
      return (
        <Stack spacing={1.5}>
          <Stack spacing={0}>
            <Text height="standard" family="medium" scale="4">
              Dresses and Tops
            </Text>
            <Text height="tall" family="regular" scale="3">
              Includes tops, sweaters, dresses, jumpsuits, jackets, and coats.
            </Text>
            <Box mt={0.5}>
              <SizeGuideTable
                businessLine="WOMENS"
                sizeSchema={selectedSubNavTab}
                tableName="dressesAndTops"
              />
            </Box>
          </Stack>
          <Stack spacing={0}>
            <Text height="standard" family="medium" scale="4">
              Bottoms
            </Text>
            <Text height="tall" family="regular" scale="3">
              Includes pants, jeans, shorts, and skirts.
            </Text>
            <Box mt={0.5}>
              <SizeGuideTable
                businessLine="WOMENS"
                sizeSchema={selectedSubNavTab}
                tableName="bottoms"
              />
            </Box>
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack spacing={1.5}>
        <Stack spacing={0}>
          <Text height="standard" family="medium" scale="4">
            Tops
          </Text>
          <Text height="tall" family="regular" scale="3">
            Includes shirts, sweaters, jackets and coats.
          </Text>
          <Box mt={0.5}>
            <SizeGuideTable
              businessLine="MENS"
              sizeSchema={selectedSubNavTab}
              tableName="tops"
            />
          </Box>
        </Stack>
        <Stack spacing={0}>
          <Text height="standard" family="medium" scale="4">
            Bottoms
          </Text>
          <Text height="tall" family="regular" scale="3">
            Includes pants, jeans, shorts, and swimwear.
          </Text>
          <Box mt={0.5}>
            <SizeGuideTable
              businessLine="MENS"
              sizeSchema={selectedSubNavTab}
              tableName="bottoms"
            />
          </Box>
        </Stack>
        <Stack spacing={0}>
          <Text height="standard" family="medium" scale="4">
            Belts
          </Text>
          <Box mt={0.5}>
            <SizeGuideTable
              businessLine="MENS"
              sizeSchema={selectedSubNavTab}
              tableName="belts"
            />
          </Box>
        </Stack>
      </Stack>
    );
  };

  return (
    <Box data-testid="size-chart-container">
      <SingleSelectButtonGroup
        name="size-chart-sub-nav"
        size="small"
        options={subNavTabs()}
        direction="horizontal"
        spacing="grouped"
        optionMinWidth={{ md: '33.3333%' }}
        value={selectedSubNavTab}
        onChange={setSelectedSubNavTab}
      />

      {bannerText() && <Banner my={1.5}>{bannerText()}</Banner>}

      <Stack spacing={1.5} mt={1}>
        <Text height="tall" family="regular" scale="3">
          All measurements are shown in inches and reflect body dimensions.
        </Text>
        {renderSizeChart()}
      </Stack>
    </Box>
  );
};

SizeChart.propTypes = {
  businessLine: PropTypes.string.isRequired,
};

export default SizeChart;
